import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import notificationSound from '../assets/sounds/notification.mp3';
import {
  EVENTS,
  ensureSingleWebSocketConnection,  // Changed from connectUnifiedWebSocket
  requestConversations as wsRequestConversations,
  dispatchGlobalEvent,
  setActiveConversation,
  // Import system notification APIs from api-chat-ws.js
  getNotificationsAPI,
  markNotificationReadAPI,
  markAllNotificationsReadAPI,

} from './api-chat-ws';

import useFirebaseNotifications from '../hooks/useFirebaseNotifications';
import { useAuth } from './authContext';


// Create context
const NotificationContext = createContext();

export const useNotification = () => useContext(NotificationContext);

export const NotificationProvider = ({ children }) => {
  // State
  const [audioInitialized, setAudioInitialized] = useState(false);
  const [totalUnreadCount, setTotalUnreadCount] = useState(0);
  const [pendingToasts, setPendingToasts] = useState([]);
  const [currentConversation, setCurrentConversation] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [conversationsById, setConversationsById] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const [isRequesting, setIsRequesting] = useState(false);

  // System Notifications State
  const [systemNotifications, setSystemNotifications] = useState([]);
  const [isLoadingSystemNotifications, setIsLoadingSystemNotifications] = useState(false);
  const [unreadSystemNotificationCount, setUnreadSystemNotificationCount] = useState(0);
  const [systemNotificationsError, setSystemNotificationsError] = useState(null);


  // const navigate = useNavigate();
  // Auth
  const { loginStatus } = useAuth();

  // Firebase notifications
  const {
    notification: firebaseNotification,
    notificationPermission,
    requestPermission: requestNotificationPermission,
    clearNotification
  } = useFirebaseNotifications();

  // Refs
  const socketRef = useRef(null);
  const lastRefreshTime = useRef(0);
  const processedEvents = useRef(new Set());
  const processedMessages = useRef(new Set());
  const currentAudio = useRef(null);
  // Add ref for connection attempt tracking outside the callback
  const connectionAttemptRef = useRef(Date.now());
  const initialLoadTimeoutRef = useRef(null); // Ref for safety timeout

  // Fetch initial system notifications
  const fetchInitialSystemNotifications = useCallback(async () => {
    if (!loginStatus?.token) return;
    setIsLoadingSystemNotifications(true);
    setSystemNotificationsError(null);
    try {
      // Use getNotificationsAPI from api-chat-ws.js
      // Assumes chatAxios in api-chat-ws.js handles authentication token
      const response = await getNotificationsAPI(); 
      if (response?.results) {
        setSystemNotifications(response.results);
        setUnreadSystemNotificationCount(response.unread_count || 0);
      } else {
        setSystemNotifications([]);
        setUnreadSystemNotificationCount(0);
      }
    } catch (error) {
      console.error('Error fetching system notifications:', error);
      setSystemNotificationsError(error);
      setSystemNotifications([]);
      setUnreadSystemNotificationCount(0);
    } finally {
      setIsLoadingSystemNotifications(false);
    }
  }, [loginStatus?.token, setIsLoadingSystemNotifications, setSystemNotificationsError, setSystemNotifications, setUnreadSystemNotificationCount]);

  // Fetch initial system notifications on login and clear on logout
  useEffect(() => {
    if (loginStatus?.token) {
      console.log("NotificationProvider: Login detected, fetching initial system notifications for header badge.");
      fetchInitialSystemNotifications();
    } else {
      // Clear system notifications if user logs out
      setSystemNotifications([]);
      setUnreadSystemNotificationCount(0);
      console.log("NotificationProvider: Logout detected, cleared system notifications.");
    }
  }, [loginStatus?.token, fetchInitialSystemNotifications]);


  // Mark a single system notification as read
  const markSystemNotificationAsRead = useCallback(async (notificationId) => {
    if (!loginStatus?.token) return false;
    try {
      // Use markNotificationReadAPI from api-chat-ws.js
      await markNotificationReadAPI(notificationId); 
      setSystemNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadSystemNotificationCount(prev => {
        const newCount = prev - 1;
        return newCount < 0 ? 0 : newCount;
      });
      dispatchGlobalEvent(EVENTS.NOTIFICATION_UPDATED, { id: notificationId, is_read: true }); // Notify other components
      return true;
    } catch (error) {
      console.error('Error marking system notification as read:', error);
      return false;
    }
  }, [loginStatus?.token, setSystemNotifications, setUnreadSystemNotificationCount]);

  // Mark all system notifications as read
  const markAllSystemNotificationsAsRead = useCallback(async () => {
    if (!loginStatus?.token) return false;
    try {
      // Use markAllNotificationsReadAPI from api-chat-ws.js
      await markAllNotificationsReadAPI(); 
      setSystemNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadSystemNotificationCount(0);
      dispatchGlobalEvent(EVENTS.NOTIFICATIONS_BULK_UPDATED, { all_read: true }); // Notify other components
      return true;
    } catch (error) {
      console.error('Error marking all system notifications as read:', error);
      return false;
    }
  }, [loginStatus?.token, setSystemNotifications, setUnreadSystemNotificationCount]);

  // Initialize the audio on component mount
  useEffect(() => {
    if (!window._notificationAudio) {
      window._notificationAudio = new Audio(notificationSound);
      window._notificationAudio.volume = 0.5;
      window._notificationAudio.load();
      console.log("Notification audio initialized globally");

      // Store reference
      currentAudio.current = window._notificationAudio;

      // Try to unlock audio context on user interaction
      const unlockAudio = () => {
        const silentPlay = window._notificationAudio.play();
        if (silentPlay !== undefined) {
          silentPlay
            .then(() => {
              console.log("Audio context unlocked");
              window._notificationAudio.pause();
              window._notificationAudio.currentTime = 0;
              setAudioInitialized(true);
            })
            .catch(err => {
              console.warn("Silent play failed:", err);
            });
        }
      };

      // Add event listeners for user interaction
      const userInteractionEvents = ['click', 'touchstart', 'keydown'];
      const unlockAudioOnUserInteraction = () => {
        unlockAudio();
        userInteractionEvents.forEach(event => {
          document.removeEventListener(event, unlockAudioOnUserInteraction);
        });
        console.log("Removed user interaction listeners after first interaction");
        setAudioInitialized(true);
      };

      userInteractionEvents.forEach(event => {
        document.addEventListener(event, unlockAudioOnUserInteraction, { once: true });
      });
    } else {
      currentAudio.current = window._notificationAudio;
      setAudioInitialized(true);
    }

    return () => {
      // No cleanup needed as audio persists globally
    };
  }, []);

  // Play notification sound
  const playNotificationSound = useCallback(() => {
    if (currentAudio.current) {
      currentAudio.current.pause();
      currentAudio.current.currentTime = 0;

      const playPromise = currentAudio.current.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => console.log("Notification sound played successfully"))
          .catch(err => console.warn("Error playing notification sound:", err));
      }
      return true;
    }
    return false;
  }, []);

  // Update document title with unread count
  const updateDocumentTitle = useCallback((count) => {
    if (count > 0) {
      document.title = `(${count}) Make Loan Eazy MLE`;
    } else {
      document.title = 'Make Loan Eazy MLE';
    }
  }, []);

  // Handle unread count updates
  const handleUnreadCountUpdate = useCallback((data) => {
    if (typeof data.total === 'number') {
      console.log(`Setting total unread count: ${data.total}`);
      setTotalUnreadCount(data.total);
      updateDocumentTitle(data.total);

      // Update individual conversation unread counts if provided
      if (data.conversations) {
        setConversationsById(prev => {
          const newById = { ...prev };
          let updated = false;

          Object.entries(data.conversations).forEach(([id, count]) => {
            if (newById[id]) {
              newById[id] = {
                ...newById[id],
                unread_count: count
              };
              updated = true;
            }
          });

          return updated ? newById : prev;
        });

        // Update conversation list with new unread counts
        setConversations(prev =>
          prev.map(c => {
            if (data.conversations[c.id] !== undefined) {
              return { ...c, unread_count: data.conversations[c.id] };
            }
            return c;
          })
        );
      }
    }
  }, [updateDocumentTitle]);

  // Handle conversation updates
  const handleConversationUpdate = useCallback((data) => {
    // Skip if no valid conversation object
    if (!data || !data.conversation) {
      console.warn('Received invalid conversation data in update handler');
      return;
    }

    const conversation = data.conversation;
    const updateType = data.update_type || 'update';

    // Skip duplicate updates
    const updateKey = `${conversation.id}-${updateType}-${conversation.updated_at || Date.now()}`;
    if (processedEvents.current.has(updateKey)) {
      console.log(`Skipping duplicate conversation update: ${updateKey}`);
      return;
    }

    // Mark this update as processed
    processedEvents.current.add(updateKey);
    setTimeout(() => processedEvents.current.delete(updateKey), 5000);

    console.log(`Processing ${updateType} for conversation ${conversation.id}`);

    switch (updateType) {
      case 'created':
        // Add new conversation to state
        setConversations(prev => {
          // Skip if already exists
          if (prev.some(c => c.id === conversation.id)) {
            return prev;
          }
          return [conversation, ...prev];
        });

        // Add to lookup object
        setConversationsById(prev => ({
          ...prev,
          [conversation.id]: conversation
        }));

        // Only notify if not created by current user
        if (String(conversation.created_by) !== String(loginStatus?.user?.id)) {
          setPendingToasts(toasts => [...toasts, {
            id: Date.now(),
            type: 'new_conversation',
            conversation: conversation,
            time: new Date()
          }]);
          playNotificationSound();
        }
        break;

      case 'update':
        // Update conversation in list
        setConversations(prev => {
          const index = prev.findIndex(c => c.id === conversation.id);
          if (index === -1) {
            // Not found, add to beginning
            return [conversation, ...prev];
          }

          // Check if the update contains meaningful changes
          const existing = prev[index];
          const hasChanges = existing.updated_at !== conversation.updated_at ||
            existing.unread_count !== conversation.unread_count ||
            existing.last_message?.id !== conversation.last_message?.id;
          if (!hasChanges) {
            return prev; // No meaningful changes
          }

          // Create a new array with the updated conversation at the beginning
          return [
            conversation,
            ...prev.filter(c => c.id !== conversation.id)
          ];
        });

        // Update lookup object if needed
        setConversationsById(prev => {
          const existing = prev[conversation.id];
          if (!existing ||
            existing.updated_at !== conversation.updated_at ||
            existing.unread_count !== conversation.unread_count) {
            return { ...prev, [conversation.id]: conversation };
          }
          return prev; // No change needed
        });
        break;

      case 'deleted':
        // Remove from conversation list
        setConversations(prev => prev.filter(c => c.id !== conversation.id));

        // Remove from lookup object
        setConversationsById(prev => {
          const newState = { ...prev };
          delete newState[conversation.id];
          return newState;
        });

        // Clear current conversation if it was the deleted one
        if (currentConversation?.id === conversation.id) {
          setCurrentConversation(null);
        }

        // Notify about deletion
        setPendingToasts(toasts => [...toasts, {
          id: Date.now(),
          type: 'conversation_deleted',
          title: 'Conversation deleted',
          message: `The conversation "${conversation.name || 'Unnamed'}" has been deleted.`,
          time: new Date()
        }]);
        break;

      default:
        console.warn(`Unknown conversation update type: ${updateType}`);
    }
  }, [loginStatus?.user?.id, playNotificationSound, currentConversation]);
  // Helper function to update a conversation with a new message
  const updateConversationWithMessage = useCallback((conversationId, message, messageText, incrementUnread) => {
    if (!conversationId) return;

    console.log(`Updating conversation ${conversationId} with message ${message.id}, increment unread: ${incrementUnread}`);

    // Update the conversations lookup table
    setConversationsById(prev => {
      const conversation = prev[conversationId];
      if (!conversation) return prev; // Skip if conversation doesn't exist

      // Skip if message is older than current last message
      if (conversation.last_message &&
        new Date(conversation.last_message.created_at) >= new Date(message.created_at)) {
        return prev;
      }

      // Calculate new unread count
      const newUnreadCount = incrementUnread
        ? (conversation.unread_count || 0) + 1
        : conversation.unread_count || 0;

      // Create updated conversation with proper timestamp to ensure sorting
      const updatedConversation = {
        ...conversation,
        last_message: {
          id: message.id,
          text: messageText, // Use the cleaned message text passed to the function
          sender_id: message.sender_id,
          sender_name: message.sender_name,
          created_at: message.created_at,
          status: message.status,
          attachments: message.attachments || []
        },
        unread_count: newUnreadCount,
        updated_at: message.created_at || new Date().toISOString()
      };

      // Forced update to UI for immediate reflection
      setTimeout(() => {
        dispatchGlobalEvent('conversation-updated', { conversation: updatedConversation });
      }, 10);

      return {
        ...prev,
        [conversationId]: updatedConversation
      };
    });

    // Now update the conversations list to move this conversation to the top
    setConversations(prev => {
      const existingIndex = prev.findIndex(c => c.id === conversationId);
      if (existingIndex === -1) return prev; // Skip if conversation not found

      const conversation = prev[existingIndex];

      // Skip update if message is older than current last_message
      if (conversation.last_message &&
        new Date(conversation.last_message.created_at) >= new Date(message.created_at)) {
        return prev;
      }

      // Calculate new unread count
      const newUnreadCount = incrementUnread
        ? (conversation.unread_count || 0) + 1
        : conversation.unread_count || 0;

      // Create updated conversation
      const updatedConversation = {
        ...conversation,
        last_message: {
          id: message.id,
          text: messageText, // Use the cleaned message text
          sender_id: message.sender_id,
          sender_name: message.sender_name,
          created_at: message.created_at,
          status: message.status,
          attachments: message.attachments || []
        },
        unread_count: newUnreadCount,
        updated_at: message.created_at || new Date().toISOString()
      };

      // Create new array with updated conversation at top
      return [
        updatedConversation,
        ...prev.filter(c => c.id !== conversationId)
      ];
    });
  }, []); // Remove unnecessary dispatchGlobalEvent dependency

  // Handle new message from WebSocket - Fix duplicate notifications and add better context awareness
  const handleNewMessage = useCallback((message) => {
    // Skip processing if already processed this message
    if (!message || !message.id || processedMessages.current.has(message.id)) return;
    
    const conversationId = String(message.conversation_id);
    const userId = String(message.sender_id);
    
    // Skip our own messages
    if (userId === String(loginStatus?.user?.id)) return;
    
    // Check if this conversation is active in ANY tab/window
    const isActiveGlobally = window[`active_conversation_${conversationId}`] === true;
    
    // Check if the tab has focus AND this is the current conversation
    const isActiveInCurrentTab = document.visibilityState === 'visible' && 
                                 currentConversation && 
                                 String(currentConversation.id) === conversationId;
    
    // Check if this conversation was recently active (within last 5 seconds)
    const lastActiveTime = window[`conversation_selected_time_${conversationId}`] || 0;
    const wasRecentlyActive = (Date.now() - lastActiveTime) < 5000;
    
    // Add message to processed set
    processedMessages.current.add(message.id);
    
    // Check if we need to hide placeholder text
    const messageText = message.text === "(attachment)" && message.attachments?.length > 0 
      ? "" // Remove placeholder for attachment-only messages
      : (message.text || ""); // Ensure we handle null text safely
    
    // Update conversation with the new message
    const isUnread = !isActiveGlobally && !isActiveInCurrentTab && !wasRecentlyActive;
    updateConversationWithMessage(
      conversationId,
      message,
      messageText, // Use the cleaned message text
      isUnread // Only increment unread count if not active
    );
    
    // Only play sound/show notification if the conversation is not active
    // and user is not actively viewing this conversation
    if (!isActiveGlobally && !isActiveInCurrentTab && !wasRecentlyActive) {
      console.log(`Playing notification for message in conversation ${conversationId}`);
      
      // Play sound if not in the active conversation
      playNotificationSound();
      
      // Update total count
      updateDocumentTitle(totalUnreadCount + 1);
      
      // Create toast notification with cleaned text
      const newToast = {
        id: `msg_${Date.now()}`,
        message: messageText || (message.attachments?.length > 0 ? "Attachment" : "(no content)"),
        sender: message.sender_name || 'Someone',
        conversation_id: conversationId,
        timestamp: new Date().toISOString()
      };
      
      setPendingToasts(prev => [...prev, newToast]);
      
      // Dispatch the toast event
      dispatchGlobalEvent('new-toast-notification', newToast);
    } else {
      console.log(`Suppressing notification for active conversation ${conversationId}`);
    }
  }, [currentConversation, loginStatus?.user?.id, playNotificationSound,
      updateDocumentTitle, updateConversationWithMessage, totalUnreadCount]); // Remove unnecessary dispatchGlobalEvent dependency

  // Handle user presence updates (individual user)
  const handleUserPresence = useCallback((data) => {
    const { userId, status } = data;
    if (!userId) return;

    const userIdStr = String(userId);

    setOnlineUsers(prev => {
      // Only update if there's a change
      const currentlyOnline = prev.has(userIdStr);
      if (status === 'online' && !currentlyOnline) {
        const newSet = new Set(prev);
        newSet.add(userIdStr);
        console.log(`User ${userIdStr} came online. Total online: ${newSet.size}`);
        return newSet;
      } else if (status !== 'online' && currentlyOnline) {
        const newSet = new Set(prev);
        newSet.delete(userIdStr);
        console.log(`User ${userIdStr} went offline. Total online: ${newSet.size}`);
        return newSet;
      }
      // No change needed
      return prev;
    });
  }, []);

  // Handle full online users list update
  const handleOnlineUsersList = useCallback((userIds) => {
    if (Array.isArray(userIds)) {
      // Create a new set efficiently
      const onlineSet = new Set(userIds.map(id => String(id)));
      // Use functional update to replace the state
      setOnlineUsers(onlineSet);
      console.log(`Received full online users list. Total online: ${onlineSet.size}`);
    }
  }, []);

  // Connect to unified WebSocket with improved race condition handling
  useEffect(() => {
    if (!loginStatus?.token) {
      setIsLoading(false); // Ensure loading is false if no token
      return;
    }

    // Update connection attempt timestamp
    const currentAttempt = Date.now();
    connectionAttemptRef.current = currentAttempt;

    // Only set loading if we don't already have conversations
    if (conversations.length === 0) {
      setIsLoading(true);
      // Safety timeout: If still loading after X seconds, force loading state off
      if (initialLoadTimeoutRef.current) clearTimeout(initialLoadTimeoutRef.current);
      initialLoadTimeoutRef.current = setTimeout(() => {
        setIsLoading(prev => {
          if (prev) {
            console.warn("Initial load timeout reached. Forcing loading state off.");
            return false;
          }
          return prev;
        });
      }, 15000); // 15 seconds safety net
    }

    // Setup WebSocket handlers
    const handlers = {
      onConnect: () => {
        // Only process if this is the most recent connection attempt
        if (connectionAttemptRef.current !== currentAttempt) {
          console.log("Ignoring stale connection callback");
          return;
        }

        console.log("Connected to unified WebSocket");
        // Clear safety timeout on successful connection
        if (initialLoadTimeoutRef.current) clearTimeout(initialLoadTimeoutRef.current);
        // Wait briefly before clearing loading state to avoid UI flashing
        setTimeout(() => {
          setIsLoading(false);
        }, 300);

        // Request conversations immediately on connection
        wsRequestConversations();

        // FIXED: Explicitly request unread counts after connection
        setTimeout(() => {
          if (window.sendWebSocketMessage) {
            window.sendWebSocketMessage({
              type: 'request_unread_counts'
            });
            console.log("Explicitly requested unread counts after connection");
          }
        }, 500);
      },

      onDisconnect: () => {
        console.log("Disconnected from unified WebSocket");
        // Optionally set loading to true if critical, or handle reconnection state
        // setIsLoading(true); // Consider if needed
      },

      onError: (error) => {
        console.error("WebSocket error:", error);
        setIsLoading(false); // Ensure loading is false on error
        if (initialLoadTimeoutRef.current) clearTimeout(initialLoadTimeoutRef.current); // Clear safety timeout
      },

      onConversationsList: (data) => {
        // Only process if this is the most recent data request
        if (connectionAttemptRef.current !== currentAttempt) {
          console.log("Ignoring stale conversations data");
          return;
        }

        console.log(`Received ${data.conversations?.length || 0} conversations`);

        if (data.conversations && Array.isArray(data.conversations)) {
          // Filter out invalid conversations
          const validConversations = data.conversations.filter(conv =>
            conv && typeof conv === 'object' && conv.id
          );

          console.log(`Processing ${validConversations.length} valid conversations`);

          // FIXED: Preserve unread counts when receiving new conversations list
          setConversations(prev => {
            // Build unread count map from previous state
            const prevUnreadMap = {};
            if (prev && prev.length > 0) {
              prev.forEach(c => {
                if (c.id && c.unread_count > 0) {
                  prevUnreadMap[c.id] = c.unread_count;
                }
              });
            }

            // Skip update if we have newer data already
            if (prev.length > 0 && prev[0].updated_at &&
              validConversations.length > 0 &&
              new Date(prev[0].updated_at) > new Date(validConversations[0].updated_at)) {
              console.log("Skipping older conversations data");
              return prev;
            }

            // Apply unread counts from previous state if not present in new data
            const updatedConversations = validConversations.map(c => {
              // If conversation has no unread_count but existed before with counts, restore it
              if (c.unread_count === 0 && prevUnreadMap[c.id] > 0) {
                console.log(`Preserving unread count for conversation ${c.id}: ${prevUnreadMap[c.id]}`);
                return { ...c, unread_count: prevUnreadMap[c.id] };
              }
              return c;
            });

            return updatedConversations;
          });

          // Create lookup object with preserved unread counts
          setConversationsById(prev => {
            const byId = {};
            validConversations.forEach(conv => {
              const existingConv = prev[conv.id];
              if (existingConv && existingConv.unread_count > 0 && conv.unread_count === 0) {
                // Preserve unread count if it was lost
                byId[conv.id] = { ...conv, unread_count: existingConv.unread_count };
              } else {
                byId[conv.id] = conv;
              }
            });
            return byId;
          });

          // FIXED: Calculate total unread count from all conversations
          const totalUnread = validConversations.reduce((total, conv) =>
            total + (conv.unread_count || 0), 0);

          // Update total unread count if it's different from what we have
          setTotalUnreadCount(prev => {
            if (prev !== totalUnread) {
              console.log(`Updating total unread count to ${totalUnread}`);
              updateDocumentTitle(totalUnread);
              return totalUnread;
            }
            return prev;
          });
        }

        // Clear loading state
        setIsLoading(false);
        setIsRequesting(false);
        if (initialLoadTimeoutRef.current) clearTimeout(initialLoadTimeoutRef.current); // Clear safety timeout
      },

      onUnreadCountUpdate: handleUnreadCountUpdate,
      onConversationUpdate: handleConversationUpdate,
      onMessage: handleNewMessage,
      onUserPresence: handleUserPresence, // Use the optimized handler
      onOnlineUsersList: handleOnlineUsersList // Use the optimized handler - removed duplicate key
    };

    // Connect to WebSocket using ensureSingleWebSocketConnection
    const socket = ensureSingleWebSocketConnection(handlers);  // Changed from connectUnifiedWebSocket
    socketRef.current = socket;

    // Cleanup on unmount
    return () => {
      // Clear safety timeout on unmount
      if (initialLoadTimeoutRef.current) clearTimeout(initialLoadTimeoutRef.current);
      // Don't disconnect on unmount to maintain persistent connection
      socketRef.current = null;
    };
  }, [loginStatus?.token, handleUnreadCountUpdate, handleConversationUpdate, handleNewMessage, handleUserPresence, handleOnlineUsersList, conversations.length, updateDocumentTitle]);

  // WebSocket event listeners for system notifications - Updated to use global events
  useEffect(() => {
    const handleNewSystemNotification = (event) => {
      const newNotification = event.detail.notification;
      
      if (!newNotification || !newNotification.id) {
        console.warn('Received invalid or null system notification data via global event:', event.detail);
        return; 
      }

      console.log('Received new system notification via global event:', newNotification);
      
      setSystemNotifications(prev => [
        newNotification, 
        ...prev.filter(n => n.id !== newNotification.id)
      ]);
      
      if (!newNotification.is_read) {
        setUnreadSystemNotificationCount(prev => prev + 1);
        playNotificationSound(); 

        // Check if it's a new match notification to create a specific toast
        // Ensure type name matches backend: 'new_match'
        if (newNotification.notification_type?.name === 'new_match') {
          const matchToast = {
            id: `match_ws_${newNotification.id}_${Date.now()}`,
            type: 'new_match', // Consistent type for toasts
            title: newNotification.title || "It's a Match!",
            message: newNotification.message,
            sender: newNotification.metadata?.matched_user_name || newNotification.sender?.first_name || 'Someone',
            profilePicture: newNotification.metadata?.matched_user_profile_picture || newNotification.sender?.profile_picture,
            deepLink: newNotification.deep_link,
            timestamp: new Date(newNotification.created_at || Date.now()).toISOString()
          };
          setPendingToasts(prevToasts => [...prevToasts, matchToast]);
        }
        // Potentially add other specific toast creations here for other notification types if needed
      }
    };

    const handleSystemNotificationUpdated = (event) => {
      const updatedNotification = event.detail;
      
      if (!updatedNotification || !updatedNotification.id) {
        console.warn('Received invalid notification update data:', updatedNotification);
        return;
      }

      console.log('Received system notification update via global event:', updatedNotification);
      
      setSystemNotifications(prev =>
        prev.map(n => (n.id === updatedNotification.id ? { ...n, ...updatedNotification } : n))
      );
      
      // Recalculate unread count based on updated notifications
      setSystemNotifications(currentNotifications => {
        const newUnreadCount = currentNotifications
          .map(n => n.id === updatedNotification.id ? { ...n, ...updatedNotification } : n)
          .filter(n => !n.is_read).length;
        setUnreadSystemNotificationCount(newUnreadCount);
        return currentNotifications.map(n => n.id === updatedNotification.id ? { ...n, ...updatedNotification } : n);
      });
    };

    const handleSystemNotificationsBulkUpdated = (event) => {
      const bulkUpdateData = event.detail;
      console.log('Received system notifications bulk update via global event:', bulkUpdateData);
      
      if (bulkUpdateData.all_read) {
        setSystemNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
        setUnreadSystemNotificationCount(0);
      } else if (bulkUpdateData.updated_ids && Array.isArray(bulkUpdateData.updated_ids)) {
        setSystemNotifications(prev => {
          const updated = prev.map(n => 
            bulkUpdateData.updated_ids.includes(n.id) ? { ...n, is_read: true } : n
          );
          // Calculate new unread count
          const newUnreadCount = updated.filter(n => !n.is_read).length;
          setUnreadSystemNotificationCount(newUnreadCount);
          return updated;
        });
      }
    };

    // Listen for global events dispatched from api-chat-ws.js
    window.addEventListener(EVENTS.NOTIFICATION_SEND, handleNewSystemNotification);
    window.addEventListener(EVENTS.NOTIFICATION_UPDATED, handleSystemNotificationUpdated);
    window.addEventListener(EVENTS.NOTIFICATIONS_BULK_UPDATED, handleSystemNotificationsBulkUpdated);

    return () => {
      window.removeEventListener(EVENTS.NOTIFICATION_SEND, handleNewSystemNotification);
      window.removeEventListener(EVENTS.NOTIFICATION_UPDATED, handleSystemNotificationUpdated);
      window.removeEventListener(EVENTS.NOTIFICATIONS_BULK_UPDATED, handleSystemNotificationsBulkUpdated);
    };
  }, [playNotificationSound]);


  // Listen for conversation request timeout
  useEffect(() => {
    const handleConversationTimeout = (event) => {
      console.log("Conversation request timed out, forcing refresh");
      // Ensure loading is false if timeout occurs
      setIsLoading(false);
      setIsRequesting(false);
      if (initialLoadTimeoutRef.current) clearTimeout(initialLoadTimeoutRef.current); // Clear safety timeout

      if (!isLoading) {
        // Try to reconnect the WebSocket
        if (socketRef.current) {
          console.log("Attempting to reconnect WebSocket");
          socketRef.current = null;

          // Connect after a short delay
          setTimeout(() => {
            // Define reconnection handlers inline to avoid undefined reference
            const reconnectHandlers = {
              onConnect: () => {
                console.log("Reconnected to WebSocket");
                // Request conversations after reconnection
                wsRequestConversations();
              },
              onError: (error) => {
                console.error("Reconnection error:", error);
                setIsLoading(false); // Ensure loading is false on error
              },
              onConversationsList: (data) => {
                console.log(`Reconnection received ${data.conversations?.length || 0} conversations`);

                if (data.conversations && Array.isArray(data.conversations)) {
                  // Update conversations list
                  setConversations(data.conversations);

                  // Create lookup object
                  const byId = {};
                  data.conversations.forEach(conv => {
                    byId[conv.id] = conv;
                  });
                  setConversationsById(byId);
                }

                setIsLoading(false);
                setIsRequesting(false);
              }
            };

            const newSocket = ensureSingleWebSocketConnection(reconnectHandlers);  // Changed from connectUnifiedWebSocket
            socketRef.current = newSocket;
          }, 1000);
        }
      }
    };

    window.addEventListener('conversation-request-timeout', handleConversationTimeout);

    return () => {
      window.removeEventListener('conversation-request-timeout', handleConversationTimeout);
    };
  }, [isLoading, setConversations, setConversationsById, setIsLoading, setIsRequesting]); // Removed wsRequestConversations dependency as it's not from component state/props

  // Handle Firebase notifications
  useEffect(() => {
    if (firebaseNotification) {
      console.log('Handling Firebase notification:', firebaseNotification);

      // Parse message data if available for chat messages
      if (firebaseNotification.data?.type === 'new_message') {
        try {
          const messageData = JSON.parse(firebaseNotification.data.message || '{}');

          if (messageData.conversation_id) {
            // Check if this is for current conversation
            const isFromCurrentUser = String(messageData.sender_id) === String(loginStatus?.user?.id);
            const isActiveConversation = currentConversation?.id === messageData.conversation_id;

            // Only create toast when message is:
            // - Not from current user AND
            // - Not in active conversation AND
            // - Document is not visible
            if (!isFromCurrentUser && !isActiveConversation && document.visibilityState !== 'visible') {
              // Check for duplicates
              const existingToast = pendingToasts.find(t =>
                t.message && t.message.id === messageData.id
              );

              if (!existingToast) {
                // Play sound
                playNotificationSound();

                // Create toast
                setPendingToasts(toasts => [...toasts, {
                  id: `msg_fcm_${messageData.id || Date.now()}`,
                  type: 'new_message', // Differentiate toast type
                  message: messageData.text || (messageData.attachments?.length > 0 ? "Attachment" : "(no content)"),
                  sender: messageData.sender_name || 'Someone',
                  conversation_id: messageData.conversation_id,
                  timestamp: new Date().toISOString(),
                  source: 'firebase'
                }]);
              }
            }
          }
        } catch (error) {
          console.error('Error parsing Firebase notification message data:', error);
        }
      } else if (firebaseNotification.data?.type === 'new_match') { // Ensure type name matches backend FCM payload: 'new_match'
        // Handle FCM push for new match
        try {
          const notificationData = firebaseNotification.data; // FCM data payload
          // For system notifications like 'new_match', the concept of 'sender_id' might be the user who completed the match,
          // or it might be a system-sent notification. We primarily care if the current user is the recipient.
          // The toast should appear if the app is not in the foreground / user is not actively looking.
          
          // We assume the FCM is targeted to the correct user, so if received while document is not visible, show it.
          if (document.visibilityState !== 'visible') {
            playNotificationSound();
            setPendingToasts(toasts => [...toasts, {
              id: `match_fcm_${notificationData.notification_id || Date.now()}`,
              type: 'new_match', // Consistent type for toasts
              title: notificationData.title || "It's a Match!",
              message: notificationData.body, // FCM 'body' is typically the message
              sender: notificationData.matched_user_name || 'Someone', // Use metadata from FCM
              profilePicture: notificationData.matched_user_profile_picture, // Use metadata from FCM
              deepLink: notificationData.deep_link, // Use metadata from FCM
              timestamp: new Date().toISOString(),
              source: 'firebase'
            }]);
          }
        } catch (error) {
          console.error('Error processing Firebase match notification data:', error);
        }
      }

      // Clear notification
      clearNotification();
    }
  }, [firebaseNotification, currentConversation, loginStatus?.user?.id, pendingToasts, playNotificationSound, clearNotification]);

  // Request conversations list with better error handling and fallback
  const requestConversations = useCallback(() => {
    // Prevent duplicate requests
    if (isRequesting) {
      return false;
    }

    // Rate limiting
    const now = Date.now();
    if (now - lastRefreshTime.current < 3000) { // 3 second minimum between requests
      console.log("Rate limiting conversation requests");
      return false;
    }

    console.log("Requesting conversations via WebSocket");
    lastRefreshTime.current = now;
    setIsRequesting(true);
    // Set loading true only if conversations are empty
    if (conversations.length === 0) {
      setIsLoading(true);
    }

    // Send request via WebSocket
    const success = wsRequestConversations();

    if (!success) {
      // Reset requesting flag after timeout in case we don't get a response
      setTimeout(() => {
        setIsRequesting(prev => {
          if (prev) {
            console.log("Auto-resetting isRequesting flag after timeout");
            // Also reset loading if still true after timeout
            setIsLoading(loadingPrev => loadingPrev ? false : loadingPrev);
            return false;
          }
          return prev;
        });
      }, 5000); // Keep timeout for isRequesting
    }

    return success;
  }, [isRequesting, conversations.length]); // Add conversations.length dependency

  // Update active conversation and sync with WebSocket
  const updateCurrentConversation = useCallback(async (conversation) => {
    // Clear any previous active conversation flags
    if (currentConversation && currentConversation.id !== conversation?.id) {
      window[`active_conversation_${currentConversation.id}`] = false;
    }

    console.log(`Setting active conversation: ${conversation?.id || 'none'}`);
    setCurrentConversation(conversation);

    // Reset previous active localStorage entry if different
    if (localStorage.getItem('activeConversationId') &&
      localStorage.getItem('activeConversationId') !== String(conversation?.id)) {
      localStorage.removeItem('activeConversationId');
    }

    if (conversation && conversation.id) {
      const conversationId = String(conversation.id);

      // Set conversation as active in multiple places for redundancy
      window.selectedConversationId = conversationId;
      window[`active_conversation_${conversationId}`] = true;
      localStorage.setItem('activeConversationId', conversationId);

      // Store timestamp of when conversation was selected
      window[`conversation_selected_time_${conversationId}`] = Date.now();

      // Create a heartbeat to keep the conversation active
      if (window[`conversation_heartbeat_${conversationId}`]) {
        clearInterval(window[`conversation_heartbeat_${conversationId}`]);
      }

      // Update active flag every 10 seconds while viewing
      window[`conversation_heartbeat_${conversationId}`] = setInterval(() => {
        window[`active_conversation_${conversationId}`] = true;
        window[`conversation_selected_time_${conversationId}`] = Date.now();
        localStorage.setItem('activeConversationId', conversationId);
      }, 10000);

      // Fix: Make sure we're working with a valid conversation object
      if (!conversation.id) {
        console.error("Invalid conversation object:", conversation);
        return;
      }

      // Enhanced logging for debugging
      console.log("Active conversation details:", {
        id: conversation.id,
        name: conversation.name || '(no name)',
        is_group: conversation.is_group,
        unread_count: conversation.unread_count || 0
      });

      // FIXED: Only mark as read if there are unread messages
      if (conversation.unread_count > 0) {
        // Mark conversation as read in local state
        setConversationsById(prev => {
          const conv = prev[conversation.id];
          if (!conv || conv.unread_count === 0) return prev;

          // Update unread count
          return {
            ...prev,
            [conversation.id]: {
              ...conv,
              unread_count: 0
            }
          };
        });

        // Update in list
        setConversations(prev =>
          prev.map(c => c.id === conversation.id ? { ...c, unread_count: 0 } : c)
        );

        // Update total unread count
        setTotalUnreadCount(prev => {
          const conversationUnreadCount = conversationsById[conversation.id]?.unread_count || 0;
          const newCount = Math.max(0, prev - conversationUnreadCount);
          updateDocumentTitle(newCount);
          return newCount;
        });
      }

      // Tell WebSocket about active conversation - with retry logic
      let success = await setActiveConversation(conversation.id);

      // Retry once if it fails
      if (!success) {
        console.log("First attempt to set active conversation failed, retrying...");
        await new Promise(resolve => setTimeout(resolve, 500));
        success = await setActiveConversation(conversation.id);
      }

      if (!success) {
        console.error("Failed to update WebSocket with active conversation");
      } else {
        // ADDED: Explicitly mark conversation as read on server
        if (window.markConversationAsRead && conversation.unread_count > 0) {
          window.markConversationAsRead(conversation.id);
        }
      }
    } else {
      // Clear all active conversations when setting to null
      Object.keys(window).forEach(key => {
        if (key.startsWith('active_conversation_')) {
          window[key] = false;
        }
        if (key.startsWith('conversation_heartbeat_')) {
          clearInterval(window[key]);
          delete window[key];
        }
      });

      window.selectedConversationId = null;
      localStorage.removeItem('activeConversationId');

      // Reset to general notification socket
      await setActiveConversation(null);
    }
  }, [currentConversation, conversationsById, updateDocumentTitle]);

  // Consume a toast notification
  const consumeToast = useCallback((toastId) => {
    setPendingToasts(toasts => toasts.filter(toast => toast.id !== toastId));
  }, []);

  // Force refresh conversations
  const forceRefresh = useCallback(() => {
    const now = Date.now();
    if (now - lastRefreshTime.current < 2000) {
      return false; // Rate limiting
    }

    console.log("Forcing conversation refresh");
    lastRefreshTime.current = now;

    // Request fresh data from server
    return requestConversations();
  }, [requestConversations]);

  // Listen for conversation_read events
  useEffect(() => {
    const handleConversationRead = (event) => {
      const { conversation_id } = event.detail;
      if (!conversation_id) return;

      console.log(`Received conversation_read event for ${conversation_id}`);

      // Update conversation in lookup
      setConversationsById(prev => {
        const conv = prev[conversation_id];
        if (!conv) return prev;

        return {
          ...prev,
          [conversation_id]: {
            ...conv,
            unread_count: 0
          }
        };
      });

      // Update conversation in list
      setConversations(prev =>
        prev.map(c => c.id === conversation_id ?
          { ...c, unread_count: 0 } : c
        )
      );

      // Update total count
      setTotalUnreadCount(prev => {
        const currentUnread = conversationsById[conversation_id]?.unread_count || 0;
        const newCount = Math.max(0, prev - currentUnread);
        updateDocumentTitle(newCount);
        return newCount;
      });
    };

    window.addEventListener('conversation_read', handleConversationRead);
    window.addEventListener('conversation_marked_read', handleConversationRead);

    return () => {
      window.removeEventListener('conversation_read', handleConversationRead);
      window.removeEventListener('conversation_marked_read', handleConversationRead);
    };
  }, [conversationsById, updateDocumentTitle]);

  // Enhanced markConversationAsRead implementation to make sure it gets reflected immediately
  const markConversationAsRead = useCallback((conversationId) => {
    if (!conversationId) {
      console.error("Cannot mark conversation as read: No conversation ID");
      return;
    }

    // Get the conversation before updates
    const conversation = conversationsById[conversationId];
    if (!conversation) {
      console.error(`Conversation ${conversationId} not found in state`);
      return;
    }

    const currentUnreadCount = conversation.unread_count || 0;

    // Only proceed if there are unread messages
    if (currentUnreadCount <= 0) {
      console.log(`Conversation ${conversationId} has no unread messages`);
      return;
    }

    console.log(`Marking conversation ${conversationId} as read (had ${currentUnreadCount} unread)`);

    // Update UI immediately for better responsiveness

    // Update in lookup object
    setConversationsById(prev => {
      const updatedConversation = prev[conversationId];
      if (!updatedConversation) return prev;

      return {
        ...prev,
        [conversationId]: {
          ...updatedConversation,
          unread_count: 0
        }
      };
    });

    // Update in conversations list
    setConversations(prev => {
      return prev.map(c =>
        c.id === conversationId ? { ...c, unread_count: 0 } : c
      );
    });

    // Update total unread count, ensuring we don't go below 0
    setTotalUnreadCount(prev => {
      const newCount = Math.max(0, prev - currentUnreadCount);
      updateDocumentTitle(newCount);

      // Broadcast the update for any components listening
      setTimeout(() => {
        dispatchGlobalEvent(EVENTS.UNREAD_COUNT_UPDATE, {
          total: newCount,
          conversations: {
            [conversationId]: 0
          }
        });
      }, 10);

      return newCount;
    });

    // Send to server using the WebSocket method
    try {
      if (window.markConversationAsRead) {
        const success = window.markConversationAsRead(conversationId);

        // Add retry logic for reliability
        if (!success) {
          console.warn(`Failed to send mark-as-read for conversation ${conversationId}, retrying...`);
          setTimeout(() => {
            window.markConversationAsRead?.(conversationId);
          }, 500);
        }
      }
    } catch (error) {
      console.error(`Error marking conversation ${conversationId} as read:`, error);
    }
  }, [conversationsById, updateDocumentTitle]);

  // ADDED: Clear conversation activity flags when switching conversations or unmounting
  useEffect(() => {
    if (!currentConversation) return;

    // Handler to clear active status when window/tab is closed or navigated away
    const handleBeforeUnload = () => {
      // Clear any active conversation flags
      if (currentConversation && currentConversation.id) {
        window[`active_conversation_${currentConversation.id}`] = false;
        window.selectedConversationId = null;
        localStorage.removeItem('activeConversationId');
      }
    };

    // Store current conversation ID to localStorage to ensure persistence
    localStorage.setItem('activeConversationId', currentConversation.id);

    // Add event listeners
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Clean up on unmount or when conversation changes
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);

      // When leaving a conversation (component unmount or conversation change),
      // clear its active status after a small delay to prevent immediate notifications
      if (currentConversation && currentConversation.id) {
        const conversationId = currentConversation.id;

        // Use a timer to delay clearing the active flag
        setTimeout(() => {
          window[`active_conversation_${conversationId}`] = false;
          // Only clear selectedConversationId if it matches this conversation
          if (window.selectedConversationId === String(conversationId)) {
            window.selectedConversationId = null;
          }
          localStorage.removeItem('activeConversationId');
        }, 1000);
      }
    };
  }, [currentConversation]);

  // Context value
  const value = {
    // State
    audioInitialized,
    totalUnreadCount,
    pendingToasts,
    currentConversation,
    conversations,
    conversationsById,
    isLoading,
    onlineUsers,

    // System Notifications
    systemNotifications,
    isLoadingSystemNotifications,
    unreadSystemNotificationCount,
    systemNotificationsError,

    // Actions
    playNotificationSound,
    setCurrentConversation: updateCurrentConversation,
    consumeToast,
    requestConversations,
    forceRefresh,
    markConversationAsRead,
    setTotalUnreadCount, // Chat unread count

    // System Notification Actions
    fetchInitialSystemNotifications, // Ensure this is the defined function
    markSystemNotificationAsRead,    // Ensure this is the defined function
    markAllSystemNotificationsAsRead, // Ensure this is the defined function


    // Firebase notifications
    notificationPermission,
    requestNotificationPermission
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationProvider;
